import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, useLanguage } from '../useTranslations';
import './CommandPalette.css';

/**
 * Module entries derived from ROUTE_CONFIG in BreadcrumbNav.
 * Each entry has a path and a translation key for the module name.
 */
const MODULE_ENTRIES = [
  { path: '/dashboard',              moduleKey: 'sidebar.dashboard' },
  { path: '/geographical-risk',      moduleKey: 'sidebar.geographicalRisk' },
  { path: '/customer-risk',          moduleKey: 'sidebar.customerRisk' },
  { path: '/bwra',                   moduleKey: 'sidebar.bwra' },
  { path: '/transaction-monitoring', moduleKey: 'sidebar.transactionMonitoring' },
  { path: '/periodic-reporting',     moduleKey: 'sidebar.periodicReporting' },
  { path: '/admin',                  moduleKey: 'sidebar.admin' },
];

function buildItems() {
  const moduleItems = MODULE_ENTRIES.map((entry) => ({
    id: entry.path,
    label: t(entry.moduleKey),
    category: 'modules',
    type: 'navigate',
    action: entry.path,
    keywords: [t(entry.moduleKey).toLowerCase()],
  }));

  const actionItems = [
    {
      id: 'action-search',
      label: t('topMenu.search'),
      category: 'actions',
      type: 'event',
      action: 'rms_open_search',
      keywords: ['search', 'sök', 'find', 'hitta', 'global'],
    },
    {
      id: 'action-glossary',
      label: t('topMenu.glossary'),
      category: 'actions',
      type: 'event',
      action: 'rms_open_glossary',
      keywords: ['glossary', 'ordlista', 'terms', 'help'],
    },
  ];

  return [...moduleItems, ...actionItems];
}

const CommandPalette = ({ isOpen, onClose }) => {
  useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const items = buildItems();

  const filtered = query.trim()
    ? items.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase()) ||
        item.keywords.some((kw) => kw.includes(query.toLowerCase()))
      )
    : items;

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const handleSelect = useCallback(
    (item) => {
      if (item.type === 'event') {
        window.dispatchEvent(new CustomEvent(item.action));
      } else {
        navigate(item.action);
      }
      onClose();
    },
    [navigate, onClose]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter' && filtered[activeIndex]) {
        handleSelect(filtered[activeIndex]);
      }
    },
    [filtered, activeIndex, handleSelect, onClose]
  );

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.querySelector('.cp-item--active');
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!isOpen) return null;

  return (
    <div
      className="cp-overlay"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-label={t('common.commandPalette.ariaLabel')}
    >
      <div
        className="cp-panel"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="cp-search-row">
          <span className="cp-search-icon" aria-hidden="true">⌘</span>
          <input
            ref={inputRef}
            className="cp-input"
            type="text"
            placeholder={t('common.commandPalette.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('common.commandPalette.ariaLabel')}
            autoComplete="off"
            spellCheck="false"
          />
          <kbd className="cp-kbd">Esc</kbd>
        </div>

        <div className="cp-results" ref={listRef} role="listbox">
          {filtered.length === 0 ? (
            <div className="cp-empty">{t('common.commandPalette.noResults')}</div>
          ) : (
            <>
              {/* Modules group */}
              {filtered.some((i) => i.category === 'modules') && (
                <>
                  <div className="cp-category-label">
                    {t('common.commandPalette.categoryModules')}
                  </div>
                  {filtered
                    .filter((i) => i.category === 'modules')
                    .map((item) => {
                      const idx = filtered.indexOf(item);
                      return (
                        <button
                          key={item.id}
                          role="option"
                          aria-selected={idx === activeIndex}
                          className={`cp-item${idx === activeIndex ? ' cp-item--active' : ''}`}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setActiveIndex(idx)}
                          type="button"
                        >
                          <span className="cp-item-icon" aria-hidden="true">→</span>
                          <span className="cp-item-label">{item.label}</span>
                          <span className="cp-item-hint">{t('common.commandPalette.actionGoTo')}</span>
                        </button>
                      );
                    })}
                </>
              )}
              {/* Actions group */}
              {filtered.some((i) => i.category === 'actions') && (
                <>
                  <div className="cp-category-label">
                    {t('common.commandPalette.categoryActions')}
                  </div>
                  {filtered
                    .filter((i) => i.category === 'actions')
                    .map((item) => {
                      const idx = filtered.indexOf(item);
                      return (
                        <button
                          key={item.id}
                          role="option"
                          aria-selected={idx === activeIndex}
                          className={`cp-item${idx === activeIndex ? ' cp-item--active' : ''}`}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setActiveIndex(idx)}
                          type="button"
                        >
                          <span className="cp-item-icon" aria-hidden="true">⚡</span>
                          <span className="cp-item-label">{item.label}</span>
                        </button>
                      );
                    })}
                </>
              )}
            </>
          )}
        </div>

        <div className="cp-footer">
          <span className="cp-footer-hint">
            <kbd className="cp-kbd cp-kbd--sm">↑↓</kbd>
            {' '}{t('common.commandPalette.pressEsc')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
